import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForecast } from '../../../contexts/ForecastContext';
import { newCampaignActions } from '../../../store';
import Budget from '../WizardBasics/Budget/Budget';
import DailyCaps from '../WizardBasics/DailyCaps/DailyCaps';
import Timing from '../WizardBasics/Timing/Timing';
import AudienceSegments from './AudienceSegments/AudienceSegments';
import CalculateForecast from './CalculateForecast';
import Providers from './ProvidersTabs/Providers';

function WizardAudiences({
    isActive,
    useSelect,
    dispatch,
    service,
    newCampaign,
    budgets,
    setSelectedScreensLocal,
    setCalculatingForecast,
    calculatingForecast,
    selectedScreens,
    currencies,
    currencyDefault,
    totalSpent,
    selectedScreensLocal,
    tierList,
    validator,
    basics,
    updateBasics,
}) {
    const { t } = useTranslation();
    const {
        forecast,
        setForecast,
        generalDemographic,
        setGeneralDemographic,
    } = useForecast();
    const [hasAnyProvider, setHasAnyProvider] = useState(false);
    const isResizeMapOn = newCampaign.isResizeMapOn;
    const navProvidersRef = useRef();

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setHours(0, 0, 0, 0);

    const hasForecastValues = () => {
        if (!forecast) {
            return false;
        }
        return Object.keys(forecast).some((key) => {
            const value = forecast[key];
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return value !== '-' && value !== null && value !== undefined;
        });
    };

    useEffect(() => {
        if (isActive && navProvidersRef.current) {
            setHasAnyProvider(navProvidersRef.current.children.length > 0);
        }
    }, [isActive]);

    // for when the campaign have a provider audience and selected other
    // country withoud provider
    if (!hasAnyProvider && newCampaign.demographics.name) {
        dispatch(newCampaignActions.setDemographics({ id: '' }));
    }

    useEffect(() => {
        if (useSelect) {
            setHasAnyProvider(true);
        }
    }, [newCampaign.stepper]);

    const [isOpen, setIsOpen] = useState(false);
    const handleUpdateSelectedScreens = (screens) => {
        setSelectedScreensLocal(screens);
        dispatch(newCampaignActions.setSelectedScreens(screens));
    };

    const handleClearScreensWithoutProvider = () => {
        setForecast((prev) => ({
            ...prev,
            screensWithoutProvider: [],
        }));
    };

    return (
        <>
            {!isActive ? (
                ''
            ) : (
                <div
                    className={`basics mt-2 h-full overflow-visible overflow-y-auto text-sm transition-transform duration-299 ${isResizeMapOn
                        ? 'transform scale-75 -translate-y-20'
                        : ''
                        }`}>
                    <div className="module rounded m-1 mb-4">
                        <div className="header flex justify-between content-center ml-1">
                            <div className="title text-xl font-extralight">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-audiences.demographics.title'
                                )}
                            </div>
                            <div
                                className={`brief font-light text-xs text-gray-400 mt-1.5 text-right ${hasForecastValues() ? 'invisible' : ''
                                    }`}>
                                {t(
                                    'sections.campaigns.new-campaign.wizard-audiences.demographics.info'
                                )}
                            </div>
                        </div>
                        <div className="body rounded-sm p-1">
                            <nav className="flex" ref={navProvidersRef}>
                                <Providers />
                            </nav>
                            {hasAnyProvider ? (
                                <>
                                    <div className="block mt-10">
                                        <AudienceSegments
                                            calculatingForecast={
                                                calculatingForecast
                                            }
                                            generalDemographic={
                                                generalDemographic
                                            }
                                            onToggleGeneralDemographic={() =>
                                                setGeneralDemographic(
                                                    (prev) => !prev
                                                )
                                            }
                                        />

                                        <div className="border-b border-gray-200 mt-6">
                                            {/* Accordion Header/Button */}
                                            <button
                                                className="flex justify-between items-center w-full text-left font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors duration-300"
                                                onClick={() =>
                                                    setIsOpen(!isOpen)
                                                }>
                                                <span>
                                                    {t(
                                                        `sections.campaigns.new-campaign.wizard-audiences.forecast.${isOpen
                                                            ? 'hide'
                                                            : 'show'
                                                        }-basic-options`
                                                    )}
                                                </span>
                                                {/* Chevron Icon - Rotates based on open state */}
                                                <svg
                                                    className={`w-4 h-4 transform transition-transform duration-300 ${isOpen
                                                        ? 'rotate-180'
                                                        : 'rotate-0'
                                                        }`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>

                                            {/* Accordion Content */}
                                            <div
                                                className={`transition-all duration-500 ease-in-out ${
                                                    isOpen
                                                        ? 'opacity-100 overflow-visible'
                                                        : 'max-h-0 opacity-0 py-0 overflow-hidden'
                                                }`}
                                                style={{
                                                    maxHeight: `${isOpen ? '500vh' : '0px'
                                                        }`,
                                                }}>
                                                <div className="-ml-4 mt-0 flex flex-col gap-4 w-full overflow-visible">
                                                    <Budget
                                                        budget={basics.budget}
                                                        budgets={budgets}
                                                        validator={validator}
                                                        totalSpent={totalSpent}
                                                        currencies={currencies}
                                                        selectedScreens={
                                                            selectedScreens
                                                        }
                                                        selectedCurrency={
                                                            basics.selectedCurrency
                                                        }
                                                        currencyDefault={
                                                            currencyDefault
                                                        }
                                                        updateBasics={updateBasics}
                                                    />
                                                    <Timing
                                                        service={service}
                                                        timing={basics.timing}
                                                        totalSpent={totalSpent}
                                                        dispatch={dispatch}
                                                        validator={validator}
                                                        onTimingChange={(value) =>
                                                            updateBasics('timing', value)
                                                        }
                                                    />
                                                    <DailyCaps
                                                        dailyCaps={
                                                            basics.dailyCaps
                                                        }
                                                        timing={basics.timing}
                                                        budget={basics.budget}
                                                        selectedCurrency={
                                                            basics.selectedCurrency
                                                        }
                                                        updateBasics={
                                                            updateBasics
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <CalculateForecast
                                            budgets={budgets}
                                            campaign={newCampaign}
                                            dispatch={dispatch}
                                            isActive={isActive}
                                            newCampaign={newCampaign}
                                            service={service}
                                            setSelectedScreensLocal={
                                                setSelectedScreensLocal
                                            }
                                            onUpdateSelectedScreens={
                                                handleUpdateSelectedScreens
                                            }
                                            setCalculatingForecast={
                                                setCalculatingForecast
                                            }
                                            calculatingForecast={
                                                calculatingForecast
                                            }
                                            selectedScreensLocal={
                                                selectedScreensLocal
                                            }
                                            tierList={tierList}
                                            totalSpent={totalSpent}
                                            generalDemographic={
                                                generalDemographic
                                            }
                                            onClearScreensWithoutProvider={
                                                handleClearScreensWithoutProvider
                                            }
                                            basics={basics}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mt-2">
                                        <span>
                                            {t(
                                                'sections.campaigns.new-campaign.wizard-audiences.no-providers-message'
                                            )}
                                            .
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

WizardAudiences.proptyTypes = {
    isActive: PropTypes.bool.isRequired,
    dispatch: PropTypes.func,
    service: PropTypes.func,
    newCampaign: PropTypes.object,
    budget: PropTypes.object,
    campaign: PropTypes.object,
    selectedScreensLocal: PropTypes.array,
    tierList: PropTypes.object,
};

export default WizardAudiences;
